/**
 * Floor plan geometry shared between the admin-web editor and (later) the POS renderer.
 *
 * Tables are positioned on a fixed grid that mirrors an iPad in landscape (4:3). All
 * coordinates/sizes are expressed in grid cells so the layout is resolution-independent.
 */

/** Grid is 4:3 with square cells, so an 8x8 table reads small like a real table. */
export const FLOOR_PLAN_COLS = 72;
export const FLOOR_PLAN_ROWS = 54;
export const DEFAULT_TABLE_CELLS = 8;
/** Smallest footprint (cells) a table or label can be resized to. */
export const MIN_PLACEMENT_CELLS = 2;
/** A new reference label drops in wide and short, like the "Bar" annotation. */
export const DEFAULT_LABEL_WIDTH_CELLS = 16;
export const DEFAULT_LABEL_HEIGHT_CELLS = 4;
/** Rotation handle snaps to this increment (degrees). */
export const TABLE_ANGLE_STEP = 15;

/** The geometry of a table footprint, in grid cells. Rotation is in degrees. */
export interface FloorPlanRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

type Point = readonly [number, number];

/** Snap a continuous cell value to the nearest whole grid cell. */
export const snapToCell = (value: number): number => Math.round(value);

/** Snap an angle (degrees) to the nearest {@link TABLE_ANGLE_STEP}, normalised to [0, 360). */
export const snapAngle = (degrees: number): number => {
  const snapped = Math.round(degrees / TABLE_ANGLE_STEP) * TABLE_ANGLE_STEP;
  return ((snapped % 360) + 360) % 360;
};

/** True when the (unrotated) footprint sits fully inside the grid. */
export const isWithinBounds = (rect: FloorPlanRect): boolean =>
  rect.x >= 0 &&
  rect.y >= 0 &&
  rect.x + rect.width <= FLOOR_PLAN_COLS &&
  rect.y + rect.height <= FLOOR_PLAN_ROWS;

/** Clamp the footprint so it stays inside the grid (keeps width/height). */
export const clampToBounds = (rect: FloorPlanRect): FloorPlanRect => ({
  ...rect,
  x: Math.min(Math.max(0, rect.x), FLOOR_PLAN_COLS - rect.width),
  y: Math.min(Math.max(0, rect.y), FLOOR_PLAN_ROWS - rect.height),
});

/** The four corner points of a (possibly rotated) rectangle, in grid cells. */
const corners = (rect: FloorPlanRect): Point[] => {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rad = (rect.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const hw = rect.width / 2;
  const hh = rect.height / 2;
  const local: Point[] = [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ];
  return local.map(([lx, ly]) => [cx + lx * cos - ly * sin, cy + lx * sin + ly * cos]);
};

/** Edge normals of a polygon — the candidate separating axes. */
const edgeNormals = (poly: Point[]): Point[] => {
  const axes: Point[] = [];
  for (let i = 0; i < poly.length; i++) {
    const point = poly[i];
    const next = poly[(i + 1) % poly.length];
    if (!point || !next) continue;
    axes.push([-(next[1] - point[1]), next[0] - point[0]]);
  }
  return axes;
};

/** Project a polygon onto an axis, returning the [min, max] interval. */
const project = (poly: Point[], axis: Point): [number, number] => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const [x, y] of poly) {
    const value = x * axis[0] + y * axis[1];
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  return [min, max];
};

/** Allow tables to sit flush against each other without counting as overlap. */
const EPSILON = 1e-6;

/**
 * Separating Axis Theorem test for two (possibly rotated) rectangles.
 * Edge-touching is not considered an overlap.
 */
export const tablesOverlap = (a: FloorPlanRect, b: FloorPlanRect): boolean => {
  const cornersA = corners(a);
  const cornersB = corners(b);
  const axes = [...edgeNormals(cornersA), ...edgeNormals(cornersB)];

  for (const axis of axes) {
    const length = Math.hypot(axis[0], axis[1]);
    if (length === 0) continue;
    const normalised: Point = [axis[0] / length, axis[1] / length];
    const [minA, maxA] = project(cornersA, normalised);
    const [minB, maxB] = project(cornersB, normalised);
    if (maxA <= minB + EPSILON || maxB <= minA + EPSILON) {
      return false;
    }
  }
  return true;
};
